import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';

import { useAccountRole } from 'src/hooks/auth/use-account-role';

import { StaleSettings } from 'src/services/ai/ai.types';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import {
  useGetOrgPromptQuery,
  usePutOrgPromptMutation,
  useGetMeetingPromptQuery,
  useGetStaleSettingsQuery,
  usePutMeetingPromptMutation,
  usePutStaleSettingsMutation,
} from 'src/services/ai/ai.service';

import AiRestrictionsCard from './ai-restrictions-card';

function PromptCard() {
  const { data, isLoading } = useGetOrgPromptQuery();
  const [putOrgPrompt, { isLoading: isSaving }] = usePutOrgPromptMutation();
  // It sets the voice of every outreach draft, so only admins can change it (JT1-1816).
  const role = useAccountRole();
  const isAdmin = role === AccountRole.Admin || role === AccountRole.Op_Manager;

  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (data) setText(data.prompt_text);
  }, [data]);

  const handleSave = async () => {
    setSaveError('');
    try {
      await putOrgPrompt({ prompt_text: text }).unwrap();
    } catch (error: any) {
      // Admin only (JT1-1816): without unwrap a refused save still showed "Saved!".
      setSaveError(error?.data?.message ?? 'The prompt could not be saved.');
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <Card>
      <CardHeader
        title="AI Persona Prompt"
        subheader="Customise how Linki behaves for your organisation. This prompt is injected into every AI call: chat, candidate matching, outreach drafts, and executive summaries."
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack gap={2}>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              placeholder={`Example:\nYou are a specialist tech recruiter in the Australian market. Always focus on culture fit, not just skills. Use a friendly, conversational tone. Use Australian English spelling. Prioritise candidates with full work rights.`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={!isAdmin}
            />
            <Typography variant="caption" color="text.secondary">
              This is Layer 2 of a 3-layer prompt. The base platform behaviour (Layer 1) and your message (Layer 3) are always included.
              Outreach drafts follow its tone and spelling, and use Australian English unless it says otherwise.
              {!isAdmin && ' Only admins can change it.'}
            </Typography>
            {saveError && (
              <Typography variant="body2" color="error">
                {saveError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || !isAdmin}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saved ? 'Saved!' : 'Save Prompt'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function MeetingPromptCard() {
  const { data, isLoading } = useGetMeetingPromptQuery();
  const [putMeetingPrompt, { isLoading: isSaving }] = usePutMeetingPromptMutation();

  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setText(data.prompt_text);
  }, [data]);

  const handleSave = async () => {
    await putMeetingPrompt({ prompt_text: text });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <Card>
      <CardHeader
        title="Meeting Summary Prompt"
        subheader="Define how meeting transcripts are summarised for your organisation. Your prompt replaces the default summary structure, so describe the whole summary you want. Applies only to meeting (Notetaker) summaries."
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack gap={2}>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              placeholder={`Example:\nWrite a short summary paragraph covering the purpose of the meeting, then bullet the key discussion points, decisions, and action items with owners. Always capture the candidate's salary expectations and notice period, and flag any culture-fit concerns. Keep summaries under 300 words.`}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              This prompt controls how your meeting summaries are written and structured — it replaces the default structure rather than adding to it. Leave blank to use the JobTetris default summary. Summaries are always saved in the standard format.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saved ? 'Saved!' : 'Save Prompt'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

const STALE_FIELDS: { key: keyof StaleSettings; label: string; help: string }[] = [
  { key: 'stale_job_days', label: 'Jobs', help: 'Active jobs with no activity for this many days are flagged stale.' },
  { key: 'stale_applicant_days', label: 'Applicants', help: 'Applicants not progressed in this many days are flagged stale.' },
  { key: 'stale_candidate_days', label: 'Candidates', help: 'Available candidates not contacted in this many days are flagged cold.' },
  { key: 'stale_deal_days', label: 'Deals', help: 'Active deals idle for this many days are flagged stale.' },
];

function StaleSettingsCard() {
  const { data, isLoading } = useGetStaleSettingsQuery();
  const [putStaleSettings, { isLoading: isSaving }] = usePutStaleSettingsMutation();

  const [values, setValues] = useState<StaleSettings>({
    stale_job_days: 7,
    stale_applicant_days: 14,
    stale_candidate_days: 30,
    stale_deal_days: 30,
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setValues(data);
  }, [data]);

  const handleSave = async () => {
    await putStaleSettings(values).unwrap();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <Card>
      <CardHeader
        title="Staleness Thresholds"
        subheader="Define how many days of inactivity make a record stale. These thresholds drive the dashboard AI nudges and Linki's pipeline insights."
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack gap={2}>
            <Grid container spacing={2}>
              {STALE_FIELDS.map(({ key, label, help }) => (
                <Grid xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`${label} (days)`}
                    helperText={help}
                    value={values[key]}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [key]: Math.max(1, Number(e.target.value) || 1) }))
                    }
                    inputProps={{ min: 1, max: 365 }}
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saved ? 'Saved!' : 'Save Thresholds'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function AiSettingsCard() {
  return (
    <Stack gap={3}>
      <AiRestrictionsCard />
      <PromptCard />
      <MeetingPromptCard />
      <StaleSettingsCard />
    </Stack>
  );
}
